import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

// WEEX has NO browser CORS (api-spot.weex.com returns no access-control-allow-origin),
// so every REST call MUST be routed through the proxy. We reuse `proxyAxios` (basic
// auth + proxy baseURL baked from env) and the same `/api/v1/proxy?url=<encoded target>`
// routing the shared SDK proxy client uses (src/shared/cex-sdk/lib/proxy-client.ts).
const WEEX_REST_BASE = 'https://api-spot.weex.com';
const WEEX_PROXY_PATH = '/api/v1/proxy';
const WEEX_WS_URL = 'wss://ws-spot.weex.com/v2/ws/public';

// WEEX v2 public WS depth channel only supports levels 15 and 200.
const WEEX_WS_DEPTH_LEVEL = 15;

// Build the full target URL with query params and route it through the proxy, mirroring
// exchangeProxyClient.get() in src/shared/cex-sdk/lib/proxy-client.ts.
const proxyGet = <T>(url: string, params: Record<string, string | number>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => search.append(key, String(value)));
  const query = search.toString();
  const targetUrl = query ? `${url}?${query}` : url;

  return proxyAxios.get<T>(WEEX_PROXY_PATH, { params: { url: targetUrl } });
};

// WEEX success code (mirrors shared/cex-sdk/exchanges/weex/rest.ts WEEX_SUCCESS_CODE).
const WEEX_SUCCESS_CODE = '00000';

// REST snapshot poll cadence (ms). Keeps panels populated even if the browser WS to
// WEEX is blocked/unsupported.
const WEEX_REST_POLL_INTERVAL = 5000;

// WEEX candle `period` values (mirror shared/cex-sdk/exchanges/weex/timeframes.ts and
// connector/weex/common/enum.go): 1m,5m,15m,30m,1h,4h,12h,1d,1w,1M.
// The shared `cexExchangesTimeframes` table carries bitget-style values for WEEX, so we
// map the page interval to the correct WEEX period locally instead.
const WEEX_PERIOD_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1m',
  '10s': '1m',
  '30s': '1m',
  '1m': '1m',
  '3m': '5m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '1h',
  '4h': '4h',
  '6h': '4h',
  '8h': '4h',
  '12h': '12h',
  '24h': '1d',
  '1d': '1d',
  '2d': '1d',
  '3d': '1d',
  '5d': '1d',
  '168h': '1w',
  '1w': '1w',
  '1M': '1M',
};

// WEEX kline WS channel period suffix per connectWebSocket interval (ms).
const WEEX_PERIOD_BY_INTERVAL_MS: Record<number, string> = {
  60000: '1m',
  300000: '5m',
  900000: '15m',
  1800000: '30m',
  3600000: '1h',
  14400000: '4h',
  43200000: '12h',
  86400000: '1d',
  604800000: '1w',
};

// WEEX exchangeInfo returns a string `tickSize` (e.g. "0.01"), not an integer
// precision. Derive the decimal-place count so the orderbook aggregation tick logic
// (shared with bitget) works.
const precisionFromTickSize = (tickSize: string | undefined): number => {
  if (!tickSize) return 1;
  const num = Number(tickSize);
  if (!Number.isFinite(num) || num <= 0) return 1;
  const [, decimals = ''] = tickSize.split('.');
  const trimmed = decimals.replace(/0+$/, '');
  return trimmed.length || 0;
};

const toTuples = (levels: { price: string; size: string }[]): [string, string][] =>
  levels.map(level => [level.price, level.size]);

export class WeexStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  weexSymbol: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.weexSymbol = `${this.pair}_SPBL`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await proxyGet<{
        code: string;
        data: { tickSize: string }[];
      }>(`${WEEX_REST_BASE}/api/v2/public/exchangeInfo`, { symbol: this.weexSymbol });

      let prec = 1;

      if (res.data?.code === WEEX_SUCCESS_CODE) {
        prec = precisionFromTickSize(res.data.data?.[0]?.tickSize);
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Weex ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const period = WEEX_PERIOD_BY_TIMEFRAME[interval] ?? '1m';
    const symbol = `${base}${quote}`.toUpperCase() + '_SPBL';

    try {
      // WEEX candles use a `period` param (no limit / no time range) and return
      // [ts_ms, open, high, low, close, baseVolume, quoteVolume].
      const result = await proxyGet<{
        code: string;
        data?: (string | number)[][];
      }>(`${WEEX_REST_BASE}/api/v2/market/candles`, { symbol, period });

      // WEEX returns ms timestamps; the chart pipeline (handleNewCharts) already
      // converts ms->seconds, so return ms unchanged like every other strategy
      // (mexc/bitget). Pre-dividing here caused a DOUBLE /1000 -> first candle = 1970.
      return (result.data.data ?? []).map(el => [
        Number(el[0]),
        String(el[1]),
        String(el[2]),
        String(el[3]),
        String(el[4]),
        String(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Weex klines error: ', error);

      return [];
    }
  };

  // WEEX exposes NO public REST trades/fills endpoint (connector/weex/market/rest only
  // has ticker/exchangeInfo/depth/candles). Live trades therefore arrive via the WS
  // `trades.SYM` channel and feed onTradeUpdate; the initial REST snapshot is empty.
  fetchTrades = async (_args: { base: string; quote: string; limit: number }) => {
    return [] as TradeSocket[];
  };

  fetchDepthSnapshot = async () => {
    try {
      // WEEX rejects a `limit` param with code 40015, so we send only the symbol.
      const res = await proxyGet<{
        code: string;
        data?: { asks: [string, string][]; bids: [string, string][] };
      }>(`${WEEX_REST_BASE}/api/v2/market/depth`, { symbol: this.weexSymbol });

      if (res.data?.code !== WEEX_SUCCESS_CODE || !res.data.data) return;

      const responseAsks = res.data.data.asks ?? [];
      const responseBids = res.data.data.bids ?? [];

      this.asks = mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc).slice(0, this.depth);
      this.bids = mergeOrderBook(responseBids, []).sort(sortOrderbookFunc).slice(0, this.depth);

      this.publishOrderbook();
    } catch (error) {
      console.log('Weex depth snapshot error: ', error);
    }
  };

  fetchTickerSnapshot = async () => {
    try {
      const res = await proxyGet<{
        code: string;
        data?: {
          lastPrice: string;
          high: string;
          low: string;
          size: string;
          value: string;
          priceChangePercent: string;
        };
      }>(`${WEEX_REST_BASE}/api/v2/market/ticker`, { symbol: this.weexSymbol });

      if (res.data?.code !== WEEX_SUCCESS_CODE || !res.data.data) return;

      const { lastPrice, high, low, size, value, priceChangePercent } = res.data.data;

      this.onTickerUpdate({
        baseVolume: size,
        change: normalizeNumber(Number(priceChangePercent) * 100),
        close: lastPrice,
        high,
        last: Number(lastPrice).toFixed(this.precision),
        low,
        quoteVolume: value,
      });
    } catch (error) {
      console.log('Weex ticker snapshot error: ', error);
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
      ),
    );
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket(WEEX_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const periodSuffix = WEEX_PERIOD_BY_INTERVAL_MS[interval] ?? '1m';
    const tickerChannel = `ticker.${this.weexSymbol}`;
    const depthChannel = `depth.${this.weexSymbol}.${WEEX_WS_DEPTH_LEVEL}`;
    const tradesChannel = `trades.${this.weexSymbol}`;
    const klineChannel = `candle.${this.weexSymbol}.${periodSuffix}`;

    // REST snapshots so the panels populate immediately and stay fresh even if the
    // browser WS to WEEX is blocked/unsupported.
    this.fetchDepthSnapshot();
    this.fetchTickerSnapshot();

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.pollInterval = setInterval(() => {
      this.fetchDepthSnapshot();
      this.fetchTickerSnapshot();
    }, WEEX_REST_POLL_INTERVAL);

    this.ws.onopen = () => {
      // WEEX subscribes one channel per message.
      ws.send(JSON.stringify({ event: 'subscribe', channel: tickerChannel }));
      ws.send(JSON.stringify({ event: 'subscribe', channel: depthChannel }));
      ws.send(JSON.stringify({ event: 'subscribe', channel: tradesChannel }));
      ws.send(JSON.stringify({ event: 'subscribe', channel: klineChannel }));
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        // WEEX application-level ping: reply with a pong echoing the time.
        if (data?.event === 'ping') {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ event: 'pong', time: data.time }));
          }
          return;
        }

        if (data?.event === 'pong' || data?.event === 'subscribe') {
          return;
        }

        const channel = data?.channel as string | undefined;

        if (channel === tickerChannel && data?.data?.[0]) {
          const { lastPrice, high, low, size, value, priceChangePercent } = data.data[0];

          this.onTickerUpdate({
            baseVolume: size,
            change: normalizeNumber(Number(priceChangePercent) * 100),
            close: lastPrice,
            high,
            last: Number(lastPrice).toFixed(this.precision),
            low,
            quoteVolume: value,
          });
        }

        if (channel === klineChannel && data?.data?.[0]) {
          const last = data.data[data.data.length - 1];
          const [time, open, high, low, close, volume] = last;

          this.klineUpdate([
            Math.floor(Number(time) / 1000),
            String(open),
            String(high),
            String(low),
            String(close),
            String(volume),
          ]);
        }

        if (channel === depthChannel && data?.data?.[0]) {
          const depthType = data.data[0].depthType as 'SNAPSHOT' | 'CHANGED';

          const responseAsks = toTuples(data.data[0].asks ?? []);
          const responseBids = toTuples(data.data[0].bids ?? []);

          const asks =
            depthType === 'SNAPSHOT'
              ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids =
            depthType === 'SNAPSHOT'
              ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks.slice(0, this.depth);
          this.bids = bids.slice(0, this.depth);

          this.publishOrderbook();
        }

        if (channel === tradesChannel && data?.data?.[0]) {
          const { time, price, size, buyerMaker } = data.data[0];

          this.onTradeUpdate({
            price,
            quantity: size,
            timestamp: time,
            // buyerMaker true => buyer is the maker => the aggressor sold.
            side: buyerMaker ? 'sell' : 'buy',
          });
        }
      } catch (error) {
        console.log('Weex socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.depthWs) {
      this.depthWs.close();
      this.depthWs = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    // WEEX depth WS is a fixed 15-level book with no server-side scaling, so the tick
    // switch is a pure client-side re-aggregation.
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;
    this.asks = this.asks.slice(0, this.depth);
    this.bids = this.bids.slice(0, this.depth);
    this.publishOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new WeexStrategy(args);

    await instance.fetch();

    return instance;
  };
}
